import { defineComponent as _defineComponent } from 'vue'
import type { PromotedSportBundleEdge, SportOffer } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import BasicButton from '@/ui-components/BasicButton.vue';
import FlagSwitcher from '@/features/sports/components/event/FlagSwitcher.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import OfferReporter from '@/features/buybox/components/OfferReporter.vue';
import { faPlay, faBell } from '@fortawesome/free-solid-svg-icons';

import { BuyboxParam } from '@/helpers/offer-helper';
import { ObjectType } from '@/@types/graphql-types';
import { AppDirection } from '@/enums/app-direction';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SponsoredClickoutsHelper } from '@/features/sponsored-recommendations/helpers/sponsored-clickouts-helper';

import { toggleMySportsNotification } from '@/features/sports/helpers/sports-notification-helper';
import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useRoute } from 'vue-router/composables';
import { useI18n } from '@/helpers/composables/useI18n';
import { useAppStore, useLanguageStore, useMetaStore, useUserStore } from '@/helpers/composables/useStores';

import { computed } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'EventBuybox',
  props: {
    eventId: null,
    eventName: null,
    eventObjectId: null,
    isNotificationActive: { type: Boolean },
    offers: { default: () => [] },
    bundles: null,
    additionalContexts: { default: () => [] }
  },
  emits: ["countrySwitcherClick", "clickOut", "clickOutBundle"],
  setup(__props: any) {

const props = __props as { eventId: string, eventName: string, eventObjectId: number, isNotificationActive: boolean, offers: SportOffer[], bundles?: PromotedSportBundleEdge[], additionalContexts: SnowplowContext[] };





const route = useRoute();
const { tc } = useI18n();
const { isLoggedIn } = useUserStore();
const { xServeTrack } = useAppStore();
const { appDirection } = useMetaStore();
const { country, webLocale } = useLanguageStore();

const { onVisibilityChanged } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

const sport = computed(() => route.meta?.sport);

const isRTL = computed(() => appDirection.value === AppDirection.RTL);

// Deduplicate offers by monetization type
const filteredOffers = computed(() => {
	const offerMap = new Map();

	props.offers.map((offer: SportOffer) => {
		// It seems that offers come through in order of quality so the last instance is set as the best one
		const key = `${offer.package.id}_${offer.monetizationType}`;
		offerMap.set(key, offer);
	});

	return Array.from(offerMap.values());
});

function baseSportOffer(clickoutUrl: ClickoutUrl): ClickoutUrl {
	return clickoutUrl
		.addContexts(props.additionalContexts)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Sport)
		.set('uct_sport', sport.value)
		.setCanary(xServeTrack.value);
}

function getOfferClickoutUrl(offer: SportOffer) {
	return new ClickoutUrl(offer).apply(baseSportOffer);
}

function getBundleClickoutUrl(bundle: PromotedSportBundleEdge) {
	return new ClickoutUrl({
		...bundle.offer,
		standardWebURL: bundle.sportPromotionUrl,
	}).apply(baseSportOffer);
}

function linkRel(offer: SportOffer) {
	return SponsoredClickoutsHelper.isSponsoredOfferGraphql(offer.package.packageId, webLocale.value)
		? 'noopener noreferrer sponsored'
		: 'noopener noreferrer';
}

function translateMonetizationType(presentationType: string, offer?: SportOffer) {
	if (offer?.package?.packageId === 695 && country.value === 'FR') return tc('WEBAPP_FREE_TRIAL');

	return {
		FLATRATE: tc('WEBAPP_FLAT'),
		FLATRATE_AND_BUY: tc('SPORTS_WEBAPP_PPV'),
		BUY: tc('SPORTS_WEBAPP_PPV'),
		RENT: tc('SPORTS_WEBAPP_PPV'),
		ADS: tc('WEBAPP_FREE'),
		FREE: tc('WEBAPP_FREE'),
	}[presentationType];
}

function translateObjectType(objectType: string) {
	return {
		HIGHLIGHT: tc('SPORTS_WEBAPP_HIGHLIGHTS'),
		MAIN_EVENT: tc('SPORTS_WEBAPP_LIVE'),
		INTERVIEW: tc('SPORTS_WEBAPP_INTERVIEW'),
	}[objectType];
}

async function notifyMe() {
	await toggleMySportsNotification(
		{
			eventId: props.eventId,
			isNotificationActive: props.isNotificationActive,
		},
		isLoggedIn.value
	);

	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'notify_me_clicked', label: 'no_offers' },
		props.additionalContexts
	);
}

return { __sfc: true,props, route, tc, isLoggedIn, xServeTrack, appDirection, country, webLocale, onVisibilityChanged, sport, isRTL, filteredOffers, baseSportOffer, getOfferClickoutUrl, getBundleClickoutUrl, linkRel, translateMonetizationType, translateObjectType, notifyMe, BasicButton, FlagSwitcher, ProviderIcon, OfferReporter, faPlay, faBell, ObjectType }
}

})